import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Label, TextInput } from 'flowbite-react';
import * as wasm from 'sched';
import { v4 as uuidv4 } from 'uuid';

const NativePersonSchema = z.object({
  name: z.string().min(1, 'Name is required'),
});

type NativePersonFormValues = z.infer<typeof NativePersonSchema>;

interface NativePersonFormProps {
  onSubmit: (command: wasm.DatabaseCommand) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
}

const NativePersonForm: React.FC<NativePersonFormProps> = ({ onSubmit, onClose, isOpen }) => {
  if (!isOpen) return null;

  const handleSubmit = async (values: NativePersonFormValues) => {
    const command: wasm.DatabaseCommand = {
      type: 'CreateItem',
      id: uuidv4(),
      item: {
        type: 'Person',
        value: {
          name: values.name,
          availability: [],
          references: [],
        },
      },
    };
    await onSubmit(command);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-cm-bg-surface p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4 text-cm-text-primary">Create Person</h2>

        <Formik
          initialValues={{ name: '' }}
          validationSchema={toFormikValidationSchema(NativePersonSchema)}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <div>
                <div className="mb-2">
                  <Label htmlFor="name">Name</Label>
                </div>
                <Field
                  as={TextInput}
                  id="name"
                  name="name"
                  autoFocus
                  placeholder="Enter name"
                />
                <ErrorMessage name="name" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <Button color="gray" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={isSubmitting}>Create Person</Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default NativePersonForm;
